// src/components/Groups/utils/duplicateDetection.ts

import { Student } from "../../../Types/Student";

export interface DuplicateMatch {
  existingStudent: Student;
  similarity: 'exact' | 'likely' | 'possible';
  reasons: string[];
}

export interface ProcessedStudentWithDuplicates {
  firstName: string;
  lastName: string;
  middleName?: string;
  gradeLevel: string;
  dateOfBirth?: string;
  parentEmail?: string;
  parentPhone?: string;
  studentId?: string; // Auto-generated, not input by user
  _id?: string;
  duplicateMatches?: DuplicateMatch[];
  isDuplicate?: boolean;
}

/**
 * Normalizes a name for comparison (removes extra spaces, converts to lowercase)
 */
const normalizeName = (name: string): string => {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Checks if two names are similar (handles common variations)
 */
const areNamesSimilar = (name1: string, name2: string): boolean => {
  const normalized1 = normalizeName(name1);
  const normalized2 = normalizeName(name2);
  
  // Exact match
  if (normalized1 === normalized2) return true;
  
  // Check for common nickname variations (basic implementation)
  const nicknames: { [key: string]: string[] } = {
    'william': ['bill', 'billy', 'will'],
    'robert': ['bob', 'bobby', 'rob'],
    'richard': ['rick', 'ricky', 'dick'],
    'michael': ['mike', 'micky'],
    'elizabeth': ['liz', 'beth', 'betty'],
    'jennifer': ['jen', 'jenny'],
    'christopher': ['chris'],
    'matthew': ['matt'],
    'anthony': ['tony'],
    'patricia': ['pat', 'patty'],
    'katherine': ['kate', 'katie', 'kathy'],
    'margaret': ['maggie', 'meg'],
  };
  
  // Check if one name is a nickname of the other
  for (const [fullName, nicks] of Object.entries(nicknames)) {
    if ((normalized1 === fullName && nicks.includes(normalized2)) ||
        (normalized2 === fullName && nicks.includes(normalized1))) {
      return true;
    }
  }
  
  return false;
};

/**
 * Detects potential duplicates for a new student against existing students
 */
export const detectDuplicates = (
  newStudent: ProcessedStudentWithDuplicates,
  existingStudents: Student[]
): DuplicateMatch[] => {
  const matches: DuplicateMatch[] = [];
  
  for (const existing of existingStudents) {
    const reasons: string[] = [];
    let similarity: 'exact' | 'likely' | 'possible' = 'possible';
    
    // Check first name similarity
    const firstNameMatch = areNamesSimilar(newStudent.firstName, existing.firstName);
    const lastNameMatch = areNamesSimilar(newStudent.lastName, existing.lastName);
    
    if (firstNameMatch && lastNameMatch) {
      reasons.push('Same first and last name');
      similarity = 'exact';
      
      // Check middle name if available
      if (newStudent.middleName && existing.middleName) {
        if (areNamesSimilar(newStudent.middleName, existing.middleName)) {
          reasons.push('Same middle name');
        } else {
          reasons.push('Different middle name');
          similarity = 'likely';
        }
      } else if (newStudent.middleName || existing.middleName) {
        reasons.push('One has middle name, other doesn\'t');
        similarity = 'likely';
      }
      
      // Check date of birth if available (very strong indicator)
      if (newStudent.dateOfBirth && existing.dateOfBirth) {
        if (newStudent.dateOfBirth === existing.dateOfBirth) {
          reasons.push('Same date of birth');
          similarity = 'exact'; // Same DOB is very strong indicator
        } else {
          reasons.push('Different date of birth');
          similarity = 'possible'; // Different DOB suggests different people
        }
      }
      
      // Check parent contact information
      if (newStudent.parentEmail && existing.parentEmail) {
        if (newStudent.parentEmail.toLowerCase() === existing.parentEmail.toLowerCase()) {
          reasons.push('Same parent email');
          if (similarity !== 'exact') similarity = 'likely';
        } else {
          reasons.push('Different parent email');
        }
      }
      
      if (newStudent.parentPhone && existing.parentPhone) {
        // Normalize phone numbers for comparison
        const normalizePhone = (phone: string) => phone.replace(/\D/g, '');
        if (normalizePhone(newStudent.parentPhone) === normalizePhone(existing.parentPhone)) {
          reasons.push('Same parent phone');
          if (similarity !== 'exact') similarity = 'likely';
        } else {
          reasons.push('Different parent phone');
        }
      }
      
      // Check grade level
      if (newStudent.gradeLevel === existing.gradeLevel) {
        reasons.push('Same grade level');
      } else {
        reasons.push('Different grade level');
        if (similarity === 'exact') similarity = 'likely';
      }
      
      // Note: Student ID is auto-generated, so we don't check it for duplicates
      
      matches.push({
        existingStudent: existing,
        similarity,
        reasons
      });
    } else if (firstNameMatch || lastNameMatch) {
      // Partial name match
      if (firstNameMatch) reasons.push('Same first name');
      if (lastNameMatch) reasons.push('Same last name');
      
      // Only consider it a possible duplicate if other factors align
      if (newStudent.gradeLevel === existing.gradeLevel) {
        reasons.push('Same grade level');
        matches.push({
          existingStudent: existing,
          similarity: 'possible',
          reasons
        });
      }
    }
  }
  
  return matches.sort((a, b) => {
    // Sort by similarity level (exact > likely > possible)
    const order = { exact: 3, likely: 2, possible: 1 };
    return order[b.similarity] - order[a.similarity];
  });
};

/**
 * Processes a list of students and detects duplicates
 */
export const processStudentsWithDuplicateDetection = (
  newStudents: ProcessedStudentWithDuplicates[],
  existingStudents: Student[]
): ProcessedStudentWithDuplicates[] => {
  return newStudents.map(student => {
    const duplicateMatches = detectDuplicates(student, existingStudents);
    return {
      ...student,
      duplicateMatches,
      isDuplicate: duplicateMatches.length > 0
    };
  });
};

/**
 * Validates student fields
 */
export const validateStudentFields = (
  firstName: string, 
  lastName: string, 
  middleName?: string,
  dateOfBirth?: string,
  parentEmail?: string,
  parentPhone?: string
) => {
  const errors: { [key: string]: string } = {};
  
  // First name validation
  if (!firstName.trim()) {
    errors.firstName = 'First name is required';
  } else if (!/^[a-zA-Z\s\-']+$/.test(firstName)) {
    errors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
  } else if (firstName.trim().length < 2) {
    errors.firstName = 'First name must be at least 2 characters';
  }
  
  // Last name validation
  if (!lastName.trim()) {
    errors.lastName = 'Last name is required';
  } else if (!/^[a-zA-Z\s\-']+$/.test(lastName)) {
    errors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
  } else if (lastName.trim().length < 2) {
    errors.lastName = 'Last name must be at least 2 characters';
  }
  
  // Middle name validation (optional)
  if (middleName && middleName.trim()) {
    if (!/^[a-zA-Z\s\-']+$/.test(middleName.trim())) {
      errors.middleName = 'Middle name can only contain letters, spaces, hyphens, and apostrophes';
    }
  }
  
  // Date of birth validation (optional but recommended)
  if (dateOfBirth && dateOfBirth.trim()) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateOfBirth)) {
      errors.dateOfBirth = 'Date of birth must be in YYYY-MM-DD format';
    } else {
      const date = new Date(dateOfBirth);
      const today = new Date();
      const minDate = new Date(today.getFullYear() - 25, today.getMonth(), today.getDate());
      const maxDate = new Date(today.getFullYear() - 3, today.getMonth(), today.getDate());
      
      if (date < minDate || date > maxDate) {
        errors.dateOfBirth = 'Date of birth should be between 3 and 25 years ago';
      }
    }
  }
  
  // Parent email validation (optional)
  if (parentEmail && parentEmail.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(parentEmail)) {
      errors.parentEmail = 'Please enter a valid email address';
    }
  }
  
  // Parent phone validation (optional)
  if (parentPhone && parentPhone.trim()) {
    const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
    if (!phoneRegex.test(parentPhone)) {
      errors.parentPhone = 'Please enter a valid phone number';
    } else {
      const digitsOnly = parentPhone.replace(/\D/g, '');
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        errors.parentPhone = 'Phone number should be 10-15 digits';
      }
    }
  }
  
  // Check if first and last names are the same
  if (firstName.trim() && lastName.trim() && 
      normalizeName(firstName) === normalizeName(lastName)) {
    errors.lastName = 'First and last names cannot be the same';
  }
  
  return errors;
};

// Keep the old function for backward compatibility
export const validateStudentNames = validateStudentFields;
